import { graphQlClient } from '@config/graphqlClient';
import WarehouseGraphQL from '@graphql/warehouse.queries';
import {
  Warehouse,
  WarehouseCreateInput,
  WarehouseUpdateInput,
  MaterialCategory,
  MaterialCategoryCreateInput,
  MaterialCategoryUpdateInput,
  MaterialCatalog,
  MaterialCatalogCreateInput,
  MaterialCatalogUpdateInput,
  StockLevel,
  StockTransaction,
  StockTransactionFilterInput,
  ReceiveStockInput,
  ConsumeStockInput,
  AdjustStockInput,
  ReserveStockInput,
  ReturnStockInput,
  TransferStockInput,
  TaskMaterial,
  TaskMaterialManualInput,
  TaskMaterialWarehouseInput,
  TaskMaterialPlannedInput,
  TaskMaterialUpdateInput,
  TaskMaterialReturnInput,
  EquipmentAsset,
  EquipmentAssetCreateInput,
  EquipmentAssetUpdateInput,
  EquipmentCheckoutInput,
  EquipmentReturnInput,
  EquipmentReserveInput,
  EquipmentMaintenanceInput,
  EquipmentUsage,
  EquipmentUsageFilterInput,
  EquipmentReservation,
  EquipmentUpdateReservationInput,
  EquipmentStatus,
  CategoryType,
  StockSummary,
  TaskMaterialSummary,
  TaskEquipment,
  TaskEquipmentManualInput,
  TaskEquipmentWarehouseInput,
  TaskEquipmentPlannedInput,
  TaskEquipmentUpdateInput,
  TaskEquipmentAssignAssetInput,
  TaskEquipmentReturnInput,
  TaskEquipmentSummary,
  LeadTimeAlert,
  LeadTimeAlertCounts,
  GetLeadTimeAlertsResponse,
  GetLeadTimeAlertCountsResponse,
} from '@models/warehouse.model';

// ==================== RESPONSE TYPES ====================

interface GetWarehousesResponse {
  getWarehouses: Warehouse[];
}

interface GetWarehouseResponse {
  getWarehouse: Warehouse;
}

interface CreateWarehouseResponse {
  createWarehouse: Warehouse;
}

interface UpdateWarehouseResponse {
  updateWarehouse: Warehouse;
}

interface SetDefaultWarehouseResponse {
  setDefaultWarehouse: Warehouse;
}

interface DeleteResponse {
  [key: string]: { affected: number };
}

interface GetMaterialCategoriesResponse {
  getMaterialCategories: MaterialCategory[];
}

interface GetMaterialCategoriesByTypeResponse {
  getMaterialCategoriesByType: MaterialCategory[];
}

interface CreateMaterialCategoryResponse {
  createMaterialCategory: MaterialCategory;
}

interface UpdateMaterialCategoryResponse {
  updateMaterialCategory: MaterialCategory;
}

interface GetMaterialCatalogResponse {
  getMaterialCatalog: MaterialCatalog[];
}

interface GetMaterialCatalogItemResponse {
  getMaterialCatalogItem: MaterialCatalog;
}

interface SearchMaterialCatalogResponse {
  searchMaterialCatalog: MaterialCatalog[];
}

interface GetMaterialCatalogByCalculatorTypeResponse {
  getMaterialCatalogByCalculatorType: MaterialCatalog[];
}

interface CreateMaterialCatalogItemResponse {
  createMaterialCatalogItem: MaterialCatalog;
}

interface UpdateMaterialCatalogItemResponse {
  updateMaterialCatalogItem: MaterialCatalog;
}

interface GetAllStockLevelsResponse {
  getAllStockLevels: StockLevel[];
}

interface GetStockLevelsByWarehouseResponse {
  getStockLevelsByWarehouse: StockLevel[];
}

interface GetStockLevelResponse {
  getStockLevel: StockLevel;
}

interface GetStockSummaryResponse {
  getStockSummary: StockSummary;
}

interface GetLowStockItemsResponse {
  getLowStockItems: StockLevel[];
}

interface StockTransactionResponse {
  [key: string]: StockTransaction;
}

interface GetStockTransactionsResponse {
  getStockTransactions: StockTransaction[];
}

interface GetTaskMaterialsResponse {
  getTaskMaterials: TaskMaterial[];
}

interface GetTaskMaterialSummaryResponse {
  getTaskMaterialSummary: TaskMaterialSummary;
}

interface TaskMaterialResponse {
  [key: string]: TaskMaterial;
}

interface GetEquipmentAssetsResponse {
  getEquipmentAssets: EquipmentAsset[];
}

interface GetEquipmentAssetsByStatusResponse {
  getEquipmentAssetsByStatus: EquipmentAsset[];
}

interface GetAvailableEquipmentResponse {
  getAvailableEquipment: EquipmentAsset[];
}

interface GetEquipmentByTaskResponse {
  getEquipmentByTask: EquipmentAsset[];
}

interface GetEquipmentAssetResponse {
  getEquipmentAsset: EquipmentAsset;
}

interface GetEquipmentUsageHistoryResponse {
  getEquipmentUsageHistory: EquipmentUsage[];
}

interface EquipmentAssetResponse {
  [key: string]: EquipmentAsset;
}

interface GetEquipmentReservationsResponse {
  getEquipmentReservations: EquipmentReservation[];
}

interface GetEquipmentReservationsByTaskResponse {
  getEquipmentReservationsByTask: EquipmentReservation[];
}

interface GetTaskEquipmentsResponse {
  getTaskEquipments: TaskEquipment[];
}

interface GetTaskEquipmentResponse {
  getTaskEquipment: TaskEquipment;
}

interface GetTaskEquipmentSummaryResponse {
  getTaskEquipmentSummary: TaskEquipmentSummary;
}

interface TaskEquipmentResponse {
  [key: string]: TaskEquipment;
}

// ==================== SERVICE ====================

export const warehouseService = {
  // ==================== WAREHOUSE ====================

  async getWarehouses(): Promise<Warehouse[]> {
    const data: GetWarehousesResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getWarehouses,
    );
    return data.getWarehouses || [];
  },

  async getWarehouse(warehouseId: string): Promise<Warehouse> {
    if (!warehouseId) throw new Error('warehouseId is required');

    const data: GetWarehouseResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getWarehouse,
      { warehouseId },
    );
    if (!data?.getWarehouse) throw new Error('Warehouse not found');
    return data.getWarehouse;
  },

  async createWarehouse(input: WarehouseCreateInput): Promise<Warehouse> {
    const data: CreateWarehouseResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createWarehouse,
      { warehouse: input },
    );
    if (!data?.createWarehouse) throw new Error('Failed to create warehouse');
    return data.createWarehouse;
  },

  async updateWarehouse(input: WarehouseUpdateInput): Promise<Warehouse> {
    if (!input.warehouseId) throw new Error('warehouseId is required');

    const data: UpdateWarehouseResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateWarehouse,
      { warehouse: input },
    );
    if (!data?.updateWarehouse) throw new Error('Failed to update warehouse');
    return data.updateWarehouse;
  },

  async setDefaultWarehouse(warehouseId: string): Promise<Warehouse> {
    if (!warehouseId) throw new Error('warehouseId is required');

    const data: SetDefaultWarehouseResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.setDefaultWarehouse,
      { warehouseId },
    );
    if (!data?.setDefaultWarehouse) throw new Error('Failed to set default warehouse');
    return data.setDefaultWarehouse;
  },

  async deleteWarehouse(warehouseId: string): Promise<{ affected: number }> {
    if (!warehouseId) throw new Error('warehouseId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteWarehouse,
      { warehouseId },
    );
    return data.deleteWarehouse;
  },

  // ==================== MATERIAL CATEGORY ====================

  async getMaterialCategories(): Promise<MaterialCategory[]> {
    const data: GetMaterialCategoriesResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getMaterialCategories,
    );
    return data.getMaterialCategories || [];
  },

  async getMaterialCategoriesByType(type: CategoryType): Promise<MaterialCategory[]> {
    const data: GetMaterialCategoriesByTypeResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getMaterialCategoriesByType,
      { type },
    );
    return data.getMaterialCategoriesByType || [];
  },

  async createMaterialCategory(input: MaterialCategoryCreateInput): Promise<MaterialCategory> {
    const data: CreateMaterialCategoryResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createMaterialCategory,
      { category: input },
    );
    if (!data?.createMaterialCategory) throw new Error('Failed to create category');
    return data.createMaterialCategory;
  },

  async updateMaterialCategory(input: MaterialCategoryUpdateInput): Promise<MaterialCategory> {
    if (!input.categoryId) throw new Error('categoryId is required');

    const data: UpdateMaterialCategoryResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateMaterialCategory,
      { category: input },
    );
    if (!data?.updateMaterialCategory) throw new Error('Failed to update category');
    return data.updateMaterialCategory;
  },

  async deleteMaterialCategory(categoryId: string): Promise<{ affected: number }> {
    if (!categoryId) throw new Error('categoryId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteMaterialCategory,
      { categoryId },
    );
    return data.deleteMaterialCategory;
  },

  // ==================== GLOBAL MATERIAL CATEGORY ====================

  async getGlobalMaterialCategories(): Promise<MaterialCategory[]> {
    const data: { getGlobalMaterialCategories: MaterialCategory[] } = await graphQlClient.request(
      WarehouseGraphQL.queries.getGlobalMaterialCategories,
    );
    return data.getGlobalMaterialCategories || [];
  },

  async createGlobalMaterialCategory(input: MaterialCategoryCreateInput): Promise<MaterialCategory> {
    const data: { createGlobalMaterialCategory: MaterialCategory } = await graphQlClient.request(
      WarehouseGraphQL.mutations.createGlobalMaterialCategory,
      { category: input },
    );
    if (!data?.createGlobalMaterialCategory) throw new Error('Failed to create global category');
    return data.createGlobalMaterialCategory;
  },

  async updateGlobalMaterialCategory(input: MaterialCategoryUpdateInput): Promise<MaterialCategory> {
    if (!input.categoryId) throw new Error('categoryId is required');

    const data: { updateGlobalMaterialCategory: MaterialCategory } = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateGlobalMaterialCategory,
      { category: input },
    );
    if (!data?.updateGlobalMaterialCategory) throw new Error('Failed to update global category');
    return data.updateGlobalMaterialCategory;
  },

  async deleteGlobalMaterialCategory(categoryId: string): Promise<{ affected: number }> {
    if (!categoryId) throw new Error('categoryId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteGlobalMaterialCategory,
      { categoryId },
    );
    return data.deleteGlobalMaterialCategory;
  },

  // ==================== MATERIAL CATALOG ====================

  async getMaterialCatalog(): Promise<MaterialCatalog[]> {
    const data: GetMaterialCatalogResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getMaterialCatalog,
    );
    return data.getMaterialCatalog || [];
  },

  async getMaterialCatalogItem(catalogId: string): Promise<MaterialCatalog> {
    if (!catalogId) throw new Error('catalogId is required');

    const data: GetMaterialCatalogItemResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getMaterialCatalogItem,
      { catalogId },
    );
    if (!data?.getMaterialCatalogItem) throw new Error('Catalog item not found');
    return data.getMaterialCatalogItem;
  },

  async searchMaterialCatalog(search: string): Promise<MaterialCatalog[]> {
    const data: SearchMaterialCatalogResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.searchMaterialCatalog,
      { search },
    );
    return data.searchMaterialCatalog || [];
  },

  async getMaterialCatalogByCalculatorType(calculatorType: string): Promise<MaterialCatalog[]> {
    if (!calculatorType) throw new Error('calculatorType is required');

    const data: GetMaterialCatalogByCalculatorTypeResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getMaterialCatalogByCalculatorType,
      { calculatorType },
    );
    return data.getMaterialCatalogByCalculatorType || [];
  },

  async createMaterialCatalogItem(input: MaterialCatalogCreateInput): Promise<MaterialCatalog> {
    const data: CreateMaterialCatalogItemResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createMaterialCatalogItem,
      { catalog: input },
    );
    if (!data?.createMaterialCatalogItem) throw new Error('Failed to create catalog item');
    return data.createMaterialCatalogItem;
  },

  async updateMaterialCatalogItem(input: MaterialCatalogUpdateInput): Promise<MaterialCatalog> {
    if (!input.catalogId) throw new Error('catalogId is required');

    const data: UpdateMaterialCatalogItemResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateMaterialCatalogItem,
      { catalog: input },
    );
    if (!data?.updateMaterialCatalogItem) throw new Error('Failed to update catalog item');
    return data.updateMaterialCatalogItem;
  },

  async deleteMaterialCatalogItem(catalogId: string): Promise<{ affected: number }> {
    if (!catalogId) throw new Error('catalogId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteMaterialCatalogItem,
      { catalogId },
    );
    return data.deleteMaterialCatalogItem;
  },

  // ==================== STOCK LEVELS ====================

  async getStockLevels(warehouseId?: string): Promise<StockLevel[]> {
    if (warehouseId) {
      const data: GetStockLevelsByWarehouseResponse = await graphQlClient.request(
        WarehouseGraphQL.queries.getStockLevelsByWarehouse,
        { warehouseId },
      );
      return data.getStockLevelsByWarehouse || [];
    }
    const data: GetAllStockLevelsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getStockLevels,
    );
    return data.getAllStockLevels || [];
  },

  async getStockLevel(catalogId: string, warehouseId: string): Promise<StockLevel> {
    if (!catalogId) throw new Error('catalogId is required');
    if (!warehouseId) throw new Error('warehouseId is required');

    const data: GetStockLevelResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getStockLevel,
      { catalogId, warehouseId },
    );
    return data.getStockLevel;
  },

  async getStockSummary(catalogId: string): Promise<StockSummary> {
    if (!catalogId) throw new Error('catalogId is required');

    const data: GetStockSummaryResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getStockSummary,
      { catalogId },
    );
    return data.getStockSummary;
  },

  async getLowStockItems(): Promise<StockLevel[]> {
    const data: GetLowStockItemsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getLowStockItems,
    );
    return data.getLowStockItems || [];
  },

  // ==================== STOCK TRANSACTIONS ====================

  async getStockTransactions(filter?: StockTransactionFilterInput): Promise<StockTransaction[]> {
    const data: GetStockTransactionsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getStockTransactions,
      { filter },
    );
    return data.getStockTransactions || [];
  },

  async receiveStock(input: ReceiveStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.receiveStock,
      { input },
    );
    if (!data?.receiveStock) throw new Error('Failed to receive stock');
    return data.receiveStock;
  },

  async consumeStock(input: ConsumeStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.consumeStock,
      { input },
    );
    if (!data?.consumeStock) throw new Error('Failed to consume stock');
    return data.consumeStock;
  },

  async adjustStock(input: AdjustStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.adjustStock,
      { input },
    );
    if (!data?.adjustStock) throw new Error('Failed to adjust stock');
    return data.adjustStock;
  },

  async reserveStock(input: ReserveStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.reserveStock,
      { input },
    );
    if (!data?.reserveStock) throw new Error('Failed to reserve stock');
    return data.reserveStock;
  },

  async releaseReservation(transactionId: string): Promise<StockTransaction> {
    if (!transactionId) throw new Error('transactionId is required');

    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.releaseReservation,
      { transactionId },
    );
    if (!data?.releaseStockReservation) throw new Error('Failed to release reservation');
    return data.releaseStockReservation;
  },

  async returnStock(input: ReturnStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.returnStock,
      { input },
    );
    if (!data?.returnStock) throw new Error('Failed to return stock');
    return data.returnStock;
  },

  async transferStock(input: TransferStockInput): Promise<StockTransaction> {
    const data: StockTransactionResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.transferStock,
      { input },
    );
    if (!data?.transferStock) throw new Error('Failed to transfer stock');
    return data.transferStock;
  },

  // ==================== TASK MATERIALS ====================

  async getTaskMaterials(taskId: string): Promise<TaskMaterial[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetTaskMaterialsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getTaskMaterials,
      { taskId },
    );
    return data.getTaskMaterials || [];
  },

  async getTaskMaterialSummary(taskId: string): Promise<TaskMaterialSummary> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetTaskMaterialSummaryResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getTaskMaterialSummary,
      { taskId },
    );
    return data.getTaskMaterialSummary;
  },

  async createTaskMaterialManual(input: TaskMaterialManualInput): Promise<TaskMaterial> {
    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskMaterialManual,
      { input },
    );
    if (!data?.createTaskMaterialManual) throw new Error('Failed to create task material');
    return data.createTaskMaterialManual;
  },

  async createTaskMaterialPlanned(input: TaskMaterialPlannedInput): Promise<TaskMaterial> {
    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskMaterialPlanned,
      { input },
    );
    if (!data?.createTaskMaterialPlanned) throw new Error('Failed to create planned material');
    return data.createTaskMaterialPlanned;
  },

  async createTaskMaterialFromWarehouse(input: TaskMaterialWarehouseInput): Promise<TaskMaterial> {
    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskMaterialFromWarehouse,
      { input },
    );
    if (!data?.createTaskMaterialFromWarehouse) throw new Error('Failed to create task material');
    return data.createTaskMaterialFromWarehouse;
  },

  async updateTaskMaterial(input: TaskMaterialUpdateInput): Promise<TaskMaterial> {
    if (!input.taskMaterialId) throw new Error('taskMaterialId is required');

    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateTaskMaterial,
      { input },
    );
    if (!data?.updateTaskMaterial) throw new Error('Failed to update task material');
    return data.updateTaskMaterial;
  },

  async returnTaskMaterial(input: TaskMaterialReturnInput): Promise<TaskMaterial> {
    if (!input.taskMaterialId) throw new Error('taskMaterialId is required');

    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.returnTaskMaterial,
      { input },
    );
    if (!data?.returnTaskMaterialToWarehouse) throw new Error('Failed to return task material');
    return data.returnTaskMaterialToWarehouse;
  },

  async deleteTaskMaterial(taskMaterialId: string): Promise<{ affected: number }> {
    if (!taskMaterialId) throw new Error('taskMaterialId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteTaskMaterial,
      { taskMaterialId },
    );
    return data.deleteTaskMaterial;
  },

  async reserveTaskMaterial(taskMaterialId: string): Promise<TaskMaterial> {
    if (!taskMaterialId) throw new Error('taskMaterialId is required');

    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.reserveTaskMaterial,
      { taskMaterialId },
    );
    return data.reserveTaskMaterial;
  },

  async consumeTaskMaterial(taskMaterialId: string): Promise<TaskMaterial> {
    if (!taskMaterialId) throw new Error('taskMaterialId is required');

    const data: TaskMaterialResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.consumeTaskMaterial,
      { taskMaterialId },
    );
    return data.consumeTaskMaterial;
  },

  // ==================== LEAD TIME ALERTS ====================

  async getLeadTimeAlerts(): Promise<LeadTimeAlert[]> {
    const data: GetLeadTimeAlertsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getLeadTimeAlerts,
    );
    return data.getLeadTimeAlerts || [];
  },

  async getLeadTimeAlertCounts(): Promise<LeadTimeAlertCounts> {
    const data: GetLeadTimeAlertCountsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getLeadTimeAlertCounts,
    );
    return data.getLeadTimeAlertCounts || { overdue: 0, urgent: 0, upcoming: 0, total: 0 };
  },

  // ==================== TASK EQUIPMENT ====================

  async getTaskEquipments(taskId: string): Promise<TaskEquipment[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetTaskEquipmentsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getTaskEquipments,
      { taskId },
    );
    return data.getTaskEquipments || [];
  },

  async getTaskEquipment(taskEquipmentId: string): Promise<TaskEquipment> {
    if (!taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: GetTaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getTaskEquipment,
      { taskEquipmentId },
    );
    if (!data?.getTaskEquipment) throw new Error('Task equipment not found');
    return data.getTaskEquipment;
  },

  async getTaskEquipmentSummary(taskId: string): Promise<TaskEquipmentSummary> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetTaskEquipmentSummaryResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getTaskEquipmentSummary,
      { taskId },
    );
    return data.getTaskEquipmentSummary;
  },

  async createTaskEquipmentManual(input: TaskEquipmentManualInput): Promise<TaskEquipment> {
    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskEquipmentManual,
      { input },
    );
    if (!data?.createTaskEquipmentManual) throw new Error('Failed to create task equipment');
    return data.createTaskEquipmentManual;
  },

  async createTaskEquipmentFromWarehouse(input: TaskEquipmentWarehouseInput): Promise<TaskEquipment> {
    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskEquipmentFromWarehouse,
      { input },
    );
    if (!data?.createTaskEquipmentFromWarehouse) throw new Error('Failed to create task equipment');
    return data.createTaskEquipmentFromWarehouse;
  },

  async createTaskEquipmentPlanned(input: TaskEquipmentPlannedInput): Promise<TaskEquipment> {
    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createTaskEquipmentPlanned,
      { input },
    );
    if (!data?.createTaskEquipmentPlanned) throw new Error('Failed to create planned equipment');
    return data.createTaskEquipmentPlanned;
  },

  async updateTaskEquipment(input: TaskEquipmentUpdateInput): Promise<TaskEquipment> {
    if (!input.taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateTaskEquipment,
      { input },
    );
    if (!data?.updateTaskEquipment) throw new Error('Failed to update task equipment');
    return data.updateTaskEquipment;
  },

  async assignTaskEquipmentAsset(input: TaskEquipmentAssignAssetInput): Promise<TaskEquipment> {
    if (!input.taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.assignTaskEquipmentAsset,
      { input },
    );
    if (!data?.assignTaskEquipmentAsset) throw new Error('Failed to assign asset');
    return data.assignTaskEquipmentAsset;
  },

  async checkoutTaskEquipment(taskEquipmentId: string): Promise<TaskEquipment> {
    if (!taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.checkoutTaskEquipment,
      { taskEquipmentId },
    );
    if (!data?.checkoutTaskEquipment) throw new Error('Failed to checkout equipment');
    return data.checkoutTaskEquipment;
  },

  async returnTaskEquipment(input: TaskEquipmentReturnInput): Promise<TaskEquipment> {
    if (!input.taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: TaskEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.returnTaskEquipment,
      { input },
    );
    if (!data?.returnTaskEquipment) throw new Error('Failed to return equipment');
    return data.returnTaskEquipment;
  },

  async deleteTaskEquipment(taskEquipmentId: string): Promise<{ affected: number }> {
    if (!taskEquipmentId) throw new Error('taskEquipmentId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteTaskEquipment,
      { taskEquipmentId },
    );
    return data.deleteTaskEquipment;
  },

  // ==================== EQUIPMENT ASSETS ====================

  async getEquipmentAssets(): Promise<EquipmentAsset[]> {
    const data: GetEquipmentAssetsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentAssets,
    );
    return data.getEquipmentAssets || [];
  },

  async getEquipmentAssetsByStatus(status: EquipmentStatus): Promise<EquipmentAsset[]> {
    const data: GetEquipmentAssetsByStatusResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentAssetsByStatus,
      { status },
    );
    return data.getEquipmentAssetsByStatus || [];
  },

  async getAvailableEquipment(): Promise<EquipmentAsset[]> {
    const data: GetAvailableEquipmentResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getAvailableEquipment,
    );
    return data.getAvailableEquipment || [];
  },

  async getEquipmentByTask(taskId: string): Promise<EquipmentAsset[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetEquipmentByTaskResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentByTask,
      { taskId },
    );
    return data.getEquipmentByTask || [];
  },

  async getEquipmentAsset(assetId: string): Promise<EquipmentAsset> {
    if (!assetId) throw new Error('assetId is required');

    const data: GetEquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentAsset,
      { assetId },
    );
    if (!data?.getEquipmentAsset) throw new Error('Equipment asset not found');
    return data.getEquipmentAsset;
  },

  async getEquipmentUsageHistory(assetId: string): Promise<EquipmentUsage[]> {
    if (!assetId) throw new Error('assetId is required');

    const data: GetEquipmentUsageHistoryResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentUsageHistory,
      { assetId },
    );
    return data.getEquipmentUsageHistory || [];
  },

  async getAllEquipmentUsageHistory(filter?: EquipmentUsageFilterInput): Promise<EquipmentUsage[]> {
    const data: { getAllEquipmentUsageHistory: EquipmentUsage[] } = await graphQlClient.request(
      WarehouseGraphQL.queries.getAllEquipmentUsageHistory,
      { filter },
    );
    return data.getAllEquipmentUsageHistory || [];
  },

  async createEquipmentAsset(input: EquipmentAssetCreateInput): Promise<EquipmentAsset> {
    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.createEquipmentAsset,
      { input },
    );
    if (!data?.createEquipmentAsset) throw new Error('Failed to create equipment asset');
    return data.createEquipmentAsset;
  },

  async updateEquipmentAsset(input: EquipmentAssetUpdateInput): Promise<EquipmentAsset> {
    if (!input.assetId) throw new Error('assetId is required');

    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateEquipmentAsset,
      { input },
    );
    if (!data?.updateEquipmentAsset) throw new Error('Failed to update equipment asset');
    return data.updateEquipmentAsset;
  },

  async checkoutEquipment(input: EquipmentCheckoutInput): Promise<EquipmentAsset> {
    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.checkoutEquipment,
      { input },
    );
    if (!data?.checkoutEquipment) throw new Error('Failed to checkout equipment');
    return data.checkoutEquipment;
  },

  async returnEquipment(input: EquipmentReturnInput): Promise<EquipmentAsset> {
    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.returnEquipment,
      { input },
    );
    if (!data?.returnEquipment) throw new Error('Failed to return equipment');
    return data.returnEquipment;
  },

  async reserveEquipment(input: EquipmentReserveInput): Promise<EquipmentAsset> {
    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.reserveEquipment,
      { input },
    );
    if (!data?.reserveEquipment) throw new Error('Failed to reserve equipment');
    return data.reserveEquipment;
  },

  async setEquipmentMaintenance(input: EquipmentMaintenanceInput): Promise<EquipmentAsset> {
    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.setEquipmentMaintenance,
      { input },
    );
    if (!data?.setEquipmentMaintenance) throw new Error('Failed to set equipment maintenance');
    return data.setEquipmentMaintenance;
  },

  async completeEquipmentMaintenance(assetId: string): Promise<EquipmentAsset> {
    if (!assetId) throw new Error('assetId is required');

    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.completeEquipmentMaintenance,
      { assetId },
    );
    if (!data?.completeEquipmentMaintenance) throw new Error('Failed to complete maintenance');
    return data.completeEquipmentMaintenance;
  },

  async cancelEquipmentReservation(reservationId: string): Promise<EquipmentAsset> {
    if (!reservationId) throw new Error('reservationId is required');

    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.cancelEquipmentReservation,
      { reservationId },
    );
    if (!data?.cancelEquipmentReservation) throw new Error('Failed to cancel reservation');
    return data.cancelEquipmentReservation;
  },

  async updateEquipmentReservation(input: EquipmentUpdateReservationInput): Promise<EquipmentAsset> {
    if (!input.reservationId) throw new Error('reservationId is required');

    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.updateEquipmentReservation,
      { input },
    );
    if (!data?.updateEquipmentReservation) throw new Error('Failed to update reservation');
    return data.updateEquipmentReservation;
  },

  async getEquipmentReservationsForRange(
    assetId: string,
    rangeStart: string | Date,
    rangeEnd: string | Date,
  ): Promise<EquipmentReservation[]> {
    if (!assetId) throw new Error('assetId is required');

    const data: { getEquipmentReservationsForRange: EquipmentReservation[] } = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentReservationsForRange,
      { assetId, rangeStart, rangeEnd },
    );
    return data?.getEquipmentReservationsForRange || [];
  },

  async getReservationsByTaskForRange(
    taskId: string,
    rangeStart: string | Date,
    rangeEnd: string | Date,
  ): Promise<EquipmentReservation[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: { getReservationsByTaskForRange: EquipmentReservation[] } = await graphQlClient.request(
      WarehouseGraphQL.queries.getReservationsByTaskForRange,
      { taskId, rangeStart, rangeEnd },
    );
    return data?.getReservationsByTaskForRange || [];
  },

  async getEquipmentReservations(assetId: string): Promise<EquipmentReservation[]> {
    if (!assetId) throw new Error('assetId is required');

    const data: GetEquipmentReservationsResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentReservations,
      { assetId },
    );
    return data?.getEquipmentReservations || [];
  },

  async getEquipmentReservationsByTask(taskId: string): Promise<EquipmentReservation[]> {
    if (!taskId) throw new Error('taskId is required');

    const data: GetEquipmentReservationsByTaskResponse = await graphQlClient.request(
      WarehouseGraphQL.queries.getEquipmentReservationsByTask,
      { taskId },
    );
    return data?.getEquipmentReservationsByTask || [];
  },

  async retireEquipment(assetId: string): Promise<EquipmentAsset> {
    if (!assetId) throw new Error('assetId is required');

    const data: EquipmentAssetResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.retireEquipment,
      { assetId },
    );
    if (!data?.retireEquipment) throw new Error('Failed to retire equipment');
    return data.retireEquipment;
  },

  async deleteEquipmentAsset(assetId: string): Promise<{ affected: number }> {
    if (!assetId) throw new Error('assetId is required');

    const data: DeleteResponse = await graphQlClient.request(
      WarehouseGraphQL.mutations.deleteEquipmentAsset,
      { assetId },
    );
    return data.deleteEquipmentAsset;
  },
};

export default warehouseService;
